

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import urlApi from '../actions/urlApi';
import { IPost, RequestParentProps, RequestProps } from 'shared/components/CommentsController/const/types';
import { Nullable, PageModel } from '@brg/api';
import { COMMENT_EDIT_MODE, INITIAL_PARENT_REQUEST_FILTER, INITIAL_REQUEST_FILTER } from 'shared/components/CommentsController/const/const';

export const fetchChatData = createAsyncThunk('chat/fetchChatData', 
    async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const { entityId, entityType } = args;
    const { filter, messages, isCreated, parentFilter } = getState().chat;
    const res = await urlApi().fetchChannelMessages({ entityId, entityType, ...filter, ...(parentFilter ? { parentId: parentFilter } : {}) })

    return res
});

export const fetchMoreChatData = createAsyncThunk('chat/fetchMoreChatData', 
    async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const { entityId, entityType } = args;
    const { filter, messages, isCreated, parentFilter } = getState().chat;
    const res = await urlApi().fetchChannelMessages({ 
        entityId, 
        entityType, 
        page: filter.page + 1,
        offset: filter.offset + 20,
        limit: 20 
    })

    return {
        paging: res.paging,
        data: [...(!!messages?.data?.length ? messages.data : []), ...res.data]
    }
});

export const fetchMoreComments = createAsyncThunk('chat/fetchMoreComments', 
    async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const { entityId, parentId, entityType, parentFilter } = args;
    const { messages } = getState().chat;
    const res = await urlApi().fetchChannelMessages({ entityId, parentId, entityType, ...parentFilter })

    const newMessages = [...messages?.data]?.map((post) => {
        if (post.id === parentId) {
            return {
                ...post,
                comments: {
                    paging: res?.paging,
                    data: [
                        ...post.comments?.data,
                        ...res?.data
                    ]
                }
            }
        }
        return post
    })

    return {
        paging: messages.paging,
        data: newMessages
    }
});

export const updateMessages = createAsyncThunk('chat/updateMessages', 
    async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const { post } = args;
    const { entityId,  entityType} = post;
    const { filter } = getState().chat;
    const res = await urlApi().fetchChannelMessages({ 
        entityId, 
        entityType, 
        offset: 0, 
        limit: filter.offset + 20,
        page: 1 
    })

    return res
});

export const updateMessage = createAsyncThunk('chat/updateMessage', 
    async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const { post, isCreated } = args;
    const { messages } = getState().chat;

    const isEdit = [...(messages.data || [])]?.find((data) => data?.id === post?.id || data?.id === post?.parentId)
    const isAnswer = !!post?.parentId

    const data = [...(messages.data || [])]
    const paging = messages?.paging

    if (isCreated) {
        const computeData = !isAnswer ? [post, ...data] : data?.map((i) => {
            if (i?.id === post?.parentId) {
                return {
                    ...i,
                    comments: {
                        ...i?.comments,
                        data: [post, ...i?.comments?.data]
                    }
                }
            }
            return i
        })
        return {
            data: computeData,
            paging: paging
        }
    } else {
        return {
            data: data?.map((i) => {
                if (i?.id === post?.parentId) {
                    return {
                        ...i,
                        comments: {
                            ...i?.comments,
                            data: i?.comments?.data?.map((c: IPost) => {
                                if (c?.id === post?.id) {
                                    return post
                                }
                                return c
                            })
                        }
                    }
                }
                if (i?.id === post?.id) {
                    return post
                }
                return i
            }),
            paging: paging
        }
    }
});


export interface ChatState {
    messages: PageModel<IPost[]> ;
    // currentPost: IPost;
    loading: boolean;
    editMode: Nullable<COMMENT_EDIT_MODE>;
    imageMode: boolean;
    answerId: Nullable<string>;
    scrollId: Nullable<string>;
    isScrolling: boolean;
    isCreated: boolean;
    filter: RequestProps;
    prevFilter: RequestProps | null
}

const INITIAL_STATE ={
    messages: {},
    loading: false,
    editing: false,
    // currentPost: null,
    imageMode: false,
    answerId: null,
    scrollId: null,
    isScrolling: false,
    isCreated: false,
    filter: INITIAL_REQUEST_FILTER,
    prevFilter: {},
}

const chatSlice = createSlice({
    name: 'chat',
    initialState: INITIAL_STATE,
    reducers: {
        onUpdateState: (state, action) => {



            // const stateKeys = Object.keys(INITIAL_STATE)

            // stateKeys.map((key) => {
            //     if (action.payload.hasOwnProperty(key)) {
                    
            //         state[key] = action.payload?.[key]
            //     }
            // })
            if (action.payload.hasOwnProperty('answerId')) {
                state.answerId = action.payload.answerId
            }
            
            // if (action.payload.hasOwnProperty('currentPost')) {
            //     state.currentPost = action.payload.currentPost
            // }
            
            if (action.payload.hasOwnProperty('imageMode')) {
                state.imageMode = action.payload.imageMode
            }
            
            if (action.payload.hasOwnProperty('scrollId')) {
                state.scrollId = action.payload.scrollId
            }

            if (action.payload.hasOwnProperty('filter')) {
                state.filter = action.payload.filter
                state.prevFilter = action.payload.filter
            }

            if (action.payload.hasOwnProperty('isCreated')) {
                state.isCreated = action.payload.isCreated
            }

            if (action.payload.hasOwnProperty('editing')) {
                state.editing = action.payload.editing
            }
        },
        setPost: (state, action) => {
            state.messages = action.payload
        },
        setScrolling: (state, action) => {
            state.isScrolling = action.payload
        },
        setPrevFilter: (state, action) => {
            state.prevFilter = action.payload
        },
        reset: (state) => {
            state = INITIAL_STATE;
        }
    },
    extraReducers: {
        [fetchChatData.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.messages = action.payload;
            state.prevFilter = {
                page: action.payload.paging?.page,
                limit: action.payload.paging?.limit,
                offset: action.payload.paging?.offset
            }
        },
        [fetchChatData.rejected.toString()]: (state) => {
            state.loading = false;
            state.messages = [];
        },
        [fetchChatData.pending.toString()]: (state) => {
            state.loading = true;
        },
        [updateMessages.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.messages = action.payload;
        },
        [updateMessages.rejected.toString()]: (state) => {
            state.loading = false;
        },
        [updateMessages.pending.toString()]: (state) => {
            state.loading = true;
        },
        [updateMessage.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.messages = action.payload;
        },
        [updateMessage.rejected.toString()]: (state) => {
            state.loading = false;
        },
        [updateMessage.pending.toString()]: (state) => {
            state.loading = true;
        },
        [fetchMoreChatData.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.messages = action.payload;
        },
        [fetchMoreChatData.rejected.toString()]: (state) => {
            state.loading = false;
            // state.messages = [];
        },
        [fetchMoreChatData.pending.toString()]: (state) => {
            state.loading = true;
        },
        [fetchMoreComments.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.messages = action.payload;
        },
        [fetchMoreComments.rejected.toString()]: (state) => {
            state.loading = false;
            // state.messages = [];
        },
        [fetchMoreComments.pending.toString()]: (state) => {
            state.loading = true;
        },
    },
});

export default chatSlice;
export const chatSliceReducer = chatSlice.reducer;
export const getChat = (store: { chat: ChatState }): ChatState => store.chat;


import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import urlApi from '../actions/urlApi';
import { IPost, RequestParentProps, RequestProps } from 'shared/components/CommentsController/const/types';
import { Nullable, PageModel } from '@brg/api';
import { COMMENT_EDIT_MODE, INITIAL_PARENT_REQUEST_FILTER, INITIAL_REQUEST_FILTER } from 'shared/components/CommentsController/const/const';

export const fetchChatData = createAsyncThunk('chat/fetchChatData', 
    async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const { entityId, entityType } = args;
    const { filter, messages, isCreated, parentFilter } = getState().chat;
    const res = await urlApi().fetchChannelMessages({ entityId, entityType, ...filter, ...(parentFilter ? { parentId: parentFilter } : {}) })

    return res
});

export const fetchMoreChatData = createAsyncThunk('chat/fetchMoreChatData', 
    async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const { entityId, entityType } = args;
    const { filter, messages, isCreated, parentFilter } = getState().chat;
    const res = await urlApi().fetchChannelMessages({ 
        entityId, 
        entityType, 
        page: filter.page + 1,
        offset: filter.offset + 20,
        limit: 20 
    })

    return {
        paging: res.paging,
        data: [...(!!messages?.data?.length ? messages.data : []), ...res.data]
    }
});

export const fetchMoreComments = createAsyncThunk('chat/fetchMoreComments', 
    async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const { entityId, parentId, entityType, parentFilter } = args;
    const { messages } = getState().chat;
    const res = await urlApi().fetchChannelMessages({ entityId, parentId, entityType, ...parentFilter })

    const newMessages = [...messages?.data]?.map((post) => {
        if (post.id === parentId) {
            return {
                ...post,
                comments: {
                    paging: res?.paging,
                    data: [
                        ...post.comments?.data,
                        ...res?.data
                    ]
                }
            }
        }
        return post
    })

    return {
        paging: messages.paging,
        data: newMessages
    }
});

export const updateMessages = createAsyncThunk('chat/updateMessages', 
    async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const { post } = args;
    const { entityId,  entityType} = post;
    const { filter } = getState().chat;
    const res = await urlApi().fetchChannelMessages({ 
        entityId, 
        entityType, 
        offset: 0, 
        limit: filter.offset + 20,
        page: 1 
    })

    return res
});

export const updateMessage = createAsyncThunk('chat/updateMessage', 
    async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const { post, isCreated } = args;
    const { messages } = getState().chat;

    const isEdit = [...(messages.data || [])]?.find((data) => data?.id === post?.id || data?.id === post?.parentId)
    const isAnswer = !!post?.parentId

    const data = [...(messages.data || [])]
    const paging = messages?.paging

    if (isCreated) {
        const computeData = !isAnswer ? [post, ...data] : data?.map((i) => {
            if (i?.id === post?.parentId) {
                return {
                    ...i,
                    comments: {
                        ...i?.comments,
                        data: [post, ...i?.comments?.data]
                    }
                }
            }
            return i
        })
        return {
            data: computeData,
            paging: paging
        }
    } else {
        return {
            data: data?.map((i) => {
                if (i?.id === post?.parentId) {
                    return {
                        ...i,
                        comments: {
                            ...i?.comments,
                            data: i?.comments?.data?.map((c: IPost) => {
                                if (c?.id === post?.id) {
                                    return post
                                }
                                return c
                            })
                        }
                    }
                }
                if (i?.id === post?.id) {
                    return post
                }
                return i
            }),
            paging: paging
        }
    }
});


export interface ChatState {
    messages: PageModel<IPost[]> ;
    // currentPost: IPost;
    loading: boolean;
    editMode: Nullable<COMMENT_EDIT_MODE>;
    imageMode: boolean;
    answerId: Nullable<string>;
    scrollId: Nullable<string>;
    isScrolling: boolean;
    isCreated: boolean;
    filter: RequestProps;
    prevFilter: RequestProps | null
}

const INITIAL_STATE ={
    messages: {},
    loading: false,
    editing: false,
    // currentPost: null,
    imageMode: false,
    answerId: null,
    scrollId: null,
    isScrolling: false,
    isCreated: false,
    filter: INITIAL_REQUEST_FILTER,
    prevFilter: {},
}

const chatSlice = createSlice({
    name: 'chat',
    initialState: INITIAL_STATE,
    reducers: {
        onUpdateState: (state, action) => {



            // const stateKeys = Object.keys(INITIAL_STATE)

            // stateKeys.map((key) => {
            //     if (action.payload.hasOwnProperty(key)) {
                    
            //         state[key] = action.payload?.[key]
            //     }
            // })
            if (action.payload.hasOwnProperty('answerId')) {
                state.answerId = action.payload.answerId
            }
            
            // if (action.payload.hasOwnProperty('currentPost')) {
            //     state.currentPost = action.payload.currentPost
            // }
            
            if (action.payload.hasOwnProperty('imageMode')) {
                state.imageMode = action.payload.imageMode
            }
            
            if (action.payload.hasOwnProperty('scrollId')) {
                state.scrollId = action.payload.scrollId
            }

            if (action.payload.hasOwnProperty('filter')) {
                state.filter = action.payload.filter
                state.prevFilter = action.payload.filter
            }

            if (action.payload.hasOwnProperty('isCreated')) {
                state.isCreated = action.payload.isCreated
            }

            if (action.payload.hasOwnProperty('editing')) {
                state.editing = action.payload.editing
            }
        },
        setPost: (state, action) => {
            state.messages = action.payload
        },
        setScrolling: (state, action) => {
            state.isScrolling = action.payload
        },
        setPrevFilter: (state, action) => {
            state.prevFilter = action.payload
        },
        reset: (state) => {
            state = INITIAL_STATE;
        }
    },
    extraReducers: {
        [fetchChatData.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.messages = action.payload;
            state.prevFilter = {
                page: action.payload.paging?.page,
                limit: action.payload.paging?.limit,
                offset: action.payload.paging?.offset
            }
        },
        [fetchChatData.rejected.toString()]: (state) => {
            state.loading = false;
            state.messages = [];
        },
        [fetchChatData.pending.toString()]: (state) => {
            state.loading = true;
        },
        [updateMessages.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.messages = action.payload;
        },
        [updateMessages.rejected.toString()]: (state) => {
            state.loading = false;
        },
        [updateMessages.pending.toString()]: (state) => {
            state.loading = true;
        },
        [updateMessage.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.messages = action.payload;
        },
        [updateMessage.rejected.toString()]: (state) => {
            state.loading = false;
        },
        [updateMessage.pending.toString()]: (state) => {
            state.loading = true;
        },
        [fetchMoreChatData.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.messages = action.payload;
        },
        [fetchMoreChatData.rejected.toString()]: (state) => {
            state.loading = false;
            // state.messages = [];
        },
        [fetchMoreChatData.pending.toString()]: (state) => {
            state.loading = true;
        },
        [fetchMoreComments.fulfilled.toString()]: (state, action) => {
            state.loading = false;
            state.messages = action.payload;
        },
        [fetchMoreComments.rejected.toString()]: (state) => {
            state.loading = false;
            // state.messages = [];
        },
        [fetchMoreComments.pending.toString()]: (state) => {
            state.loading = true;
        },
    },
});

export default chatSlice;
export const chatSliceReducer = chatSlice.reducer;
export const getChat = (store: { chat: ChatState }): ChatState => store.chat;
